import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getInstitutionTranslation, addInstitutionTranslation, saveTranslationsToFile } from '../../services/institutionsTranslationsService';
import { getDistrictTranslation } from '../../services/districtsTranslationsService';
import { getAcademyTranslation } from '../../services/academiesTranslationsService';
import { getRegionTranslation } from '../../services/regionsTranslationsService';
import { getCountryTranslation } from '../../services/countriesTranslationsService';
import { 
  BuildingIcon, 
  SearchIcon, 
  XCircleIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeIcon,
  MapPinIcon,
  PhoneIcon,
  MailIcon
} from 'lucide-react';

const Institutions = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  
  // State management
  const [institutions, setInstitutions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const LIMIT_OPTIONS = [10, 20, 50, 100];
  const [districtId, setDistrictId] = useState('');
  
  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newInstitution, setNewInstitution] = useState({
    districtId: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    translations: {
      ar: '',
      fr: '',
      en: ''
    }
  });
  const [formErrors, setFormErrors] = useState({});

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingInstitution, setEditingInstitution] = useState({
    id: '',
    name: '',
    code: '',
    districtId: '',
    address: '',
    phone: '',
    email: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // Fetch institutions - API: /institutions or /districts/{id}/institutions
  const fetchInstitutions = async (page = 1, search = '', filterDistrictId = districtId, itemsPerPage = limit) => {
    setLoading(true);
    try {
      let url = '/organization/institutions';
      if (filterDistrictId) url = `/organization/districts/${filterDistrictId}/institutions`;
      const params = new URLSearchParams({ page: page.toString(), limit: itemsPerPage.toString() });
      if (search.trim()) params.set('search', search.trim());
      const response = await api.get(`${url}?${params.toString()}`);
      
      if (response.data.success) {
        setInstitutions(response.data.data.institutions || []);
        setTotal(response.data.data.pagination.total || 0);
        setCurrentPage(response.data.data.pagination.page || 1);
      }
    } catch (error) {
      console.error('Error fetching institutions:', error);
      toast.error(t('institutions.fetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Fetch districts
  const fetchDistricts = async () => {
    setDistrictsLoading(true);
    try {
      let allDistricts = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await api.get(`/organization/districts?page=${currentPage}&limit=100`);
        if (response.data.success) {
          const fetchedDistricts = response.data.data.districts || [];
          allDistricts = [...allDistricts, ...fetchedDistricts];
          
          // Check if there are more pages
          const pagination = response.data.data.pagination;
          if (pagination && currentPage < pagination.pages) {
            currentPage++;
          } else {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      setDistricts(allDistricts);
    } catch (error) {
      console.error('Error fetching districts:', error);
      toast.error(t('institutions.districtsFetchError'), { duration: 3000, position: 'top-center' });
    } finally {
      setDistrictsLoading(false);
    }
  };

  useEffect(() => { fetchDistricts(); }, []);

  useEffect(() => {
    if (searchTerm === '') {
      fetchInstitutions(1, '', districtId);
      return;
    }
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      fetchInstitutions(1, searchTerm, districtId).finally(() => setIsSearching(false));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, districtId]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchInstitutions(1, searchTerm, districtId);
  };

  // Translate names
  const translateInstitutionName = (name, language) => {
    return getInstitutionTranslation(name, language);
  };

  const translateDistrictName = (name, language) => {
    return getDistrictTranslation(name, language);
  };

  const translateAcademyName = (name, language) => {
    return getAcademyTranslation(name, language);
  };

  const translateRegionName = (name, language) => {
    return getRegionTranslation(name, language);
  };

  const translateCountryName = (name, language) => {
    return getCountryTranslation(name, language);
  };

  const clearFilters = () => {
    setDistrictId('');
    setSearchTerm('');
    setCurrentPage(1);
    fetchInstitutions(1, '', '', limit);
  };

  const getActiveFiltersCount = () => (districtId ? 1 : 0);

  const didMountInstitutions = useRef(false);
  useEffect(() => {
    if (!didMountInstitutions.current) { didMountInstitutions.current = true; return; }
    setCurrentPage(1);
    fetchInstitutions(1, searchTerm, districtId, limit);
  }, [districtId, limit]);

  // Open add modal
  const openAddModal = () => {
    setNewInstitution({
      districtId: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      translations: {
        ar: '',
        fr: '',
        en: ''
      }
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewInstitution({
      districtId: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      translations: {
        ar: '',
        fr: '',
        en: ''
      }
    });
    setFormErrors({});
  };

  // Handle export translations
  const handleExportTranslations = () => {
    const result = saveTranslationsToFile();
    if (result.success) {
      toast.success(result.message, {
        duration: 5000,
        position: 'top-center'
      });
    } else {
      toast.error(result.message, {
        duration: 3000,
        position: 'top-center'
      });
    }
  };

  // Add institution
  const addInstitution = async () => {
    const errors = {};
    if (!newInstitution.districtId) {
      errors.districtId = t('institutions.districtRequired');
    }
    if (!newInstitution.code.trim()) {
      errors.code = t('institutions.codeRequired');
    } else if (newInstitution.code.trim().length < 2 || newInstitution.code.trim().length > 10) {
      errors.code = t('institutions.codeLength');
    }
    if (!newInstitution.address.trim()) {
      errors.address = t('institutions.addressRequired');
    }
    if (!newInstitution.phone.trim()) {
      errors.phone = t('institutions.phoneRequired');
    }
    if (!newInstitution.email.trim()) {
      errors.email = t('institutions.emailRequired');
    }
    if (!newInstitution.translations.ar.trim()) {
      errors.translation_ar = t('institutions.nameRequired_ar');
    }
    if (!newInstitution.translations.fr.trim()) {
      errors.translation_fr = t('institutions.nameRequired_fr');
    }
    if (!newInstitution.translations.en.trim()) {
      errors.translation_en = t('institutions.nameRequired_en');
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsAdding(true);
    try {
      const requestData = {
        districtId: parseInt(newInstitution.districtId),
        name: newInstitution.translations.ar.trim(),
        code: newInstitution.code.trim(),
        address: newInstitution.address.trim(),
        phone: newInstitution.phone.trim(),
        email: newInstitution.email.trim()
      };
      
      const response = await api.post('/organization/institutions', requestData);
      
      if (response.data.success) {
        const translationResult = addInstitutionTranslation(
          newInstitution.translations.ar.trim(),
          {
            ar: newInstitution.translations.ar.trim(),
            fr: newInstitution.translations.fr.trim(),
            en: newInstitution.translations.en.trim()
          }
        );

        if (translationResult.success) {
          console.log('Translation saved successfully:', translationResult.message);
        }
        
        toast.success(response.data.message || t('institutions.addSuccess'), { duration: 3000, position: 'top-center' });
        closeAddModal();
        fetchInstitutions(currentPage, searchTerm, districtId);
      }
    } catch (error) {
      console.error('Error adding institution:', error);
      const errorMessage = error.response?.data?.message || error.message || t('institutions.addError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsAdding(false);
    }
  };

  // Handle edit institution
  const handleEditInstitution = (institution) => {
    setEditingInstitution({
      id: institution.id,
      name: institution.name,
      code: institution.code,
      districtId: institution.district_id || '',
      address: institution.address || '',
      phone: institution.phone || '',
      email: institution.email || ''
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  // Update institution
  const updateInstitution = async () => {
    const errors = {};
    if (!editingInstitution.name.trim()) {
      errors.name = t('institutions.nameRequired');
    }
    if (!editingInstitution.code.trim()) {
      errors.code = t('institutions.codeRequired');
    } else if (editingInstitution.code.trim().length < 2 || editingInstitution.code.trim().length > 10) {
      errors.code = t('institutions.codeLength');
    }
    if (!editingInstitution.address.trim()) {
      errors.address = t('institutions.addressRequired');
    }
    if (!editingInstitution.phone.trim()) {
      errors.phone = t('institutions.phoneRequired');
    }
    if (!editingInstitution.email.trim()) {
      errors.email = t('institutions.emailRequired');
    }
    
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setIsEditing(true);
    try {
      const requestData = {
        name: editingInstitution.name.trim(),
        code: editingInstitution.code.trim(),
        address: editingInstitution.address.trim(),
        phone: editingInstitution.phone.trim(),
        email: editingInstitution.email.trim()
      };
      
      const response = await api.put(`/organization/institutions/${editingInstitution.id}`, requestData);
      
      if (response.data.success) {
        toast.success(response.data.message || t('institutions.updateSuccess'), { duration: 3000, position: 'top-center' });
        setShowEditModal(false);
        setEditingInstitution({
          id: '',
          name: '',
          code: '',
          districtId: '',
          address: '',
          phone: '',
          email: ''
        });
        setEditFormErrors({});
        fetchInstitutions(currentPage, searchTerm, districtId);
      }
    } catch (error) {
      console.error('Error updating institution:', error);
      const errorMessage = error.response?.data?.message || error.message || t('institutions.updateError');
      toast.error(errorMessage, { duration: 3000, position: 'top-center' });
    } finally {
      setIsEditing(false);
    }
  };

  // Delete institution
  const deleteInstitution = async (institutionId, institutionName) => {
    if (window.confirm(t('institutions.deleteConfirm', { name: institutionName }))) {
      try {
        const response = await api.delete(`/organization/institutions/${institutionId}`);
        
        if (response.data.success) {
          toast.success(response.data.message || t('institutions.deleteSuccess'), { duration: 3000, position: 'top-center' });
          fetchInstitutions(currentPage, searchTerm, districtId);
        }
      } catch (error) {
        console.error('Error deleting institution:', error);
        const errorMessage = error.response?.data?.message || error.message || t('institutions.deleteError');
        toast.error(errorMessage, { duration: 3000, position: 'top-center' });
      }
    }
  };

  // Pagination
  const totalPages = Math.ceil(total / limit);
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchInstitutions(page, searchTerm, districtId);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary-100 rounded-lg">
                <BuildingIcon className="h-6 w-6 text-primary-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('institutions.title')}
              </h1>
            </div>
            <p className="text-gray-600">{t('institutions.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={openAddModal}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              {t('institutions.addInstitution')}
            </button>
            <button
              onClick={handleExportTranslations}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <GlobeIcon className="h-5 w-5" />
              {t('institutions.exportTranslations')}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters - Modern Design */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <form onSubmit={handleSearch} className="p-4">
          <div className={`flex flex-col lg:flex-row gap-4 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
            <div className="flex-1 min-w-0">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('common.search')}</label>
              <div className="relative">
                <SearchIcon className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <input type="text" placeholder={t('institutions.searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors`} />
                {isSearching && <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'}`}><div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" /></div>}
              </div>
            </div>
            <div className="lg:w-48">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('institutions.district')}</label>
              <select value={districtId} onChange={(e) => setDistrictId(e.target.value)} disabled={districtsLoading} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                <option value="">{districtsLoading ? t('institutions.loadingDistricts') : t('institutions.allDistricts')}</option>
                {districts.map((district) => <option key={district.id} value={district.id}>{translateDistrictName(district.name, currentLanguage || 'fr')}</option>)}
              </select>
            </div>
            <div className="lg:w-32">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{t('users.perPage')}</label>
              <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} className="w-full py-2.5 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-gray-50/50 hover:bg-white transition-colors">
                {LIMIT_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-2 lg:flex-shrink-0">
              <button type="submit" className="px-4 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors">{t('common.search')}</button>
              {(districtId || searchTerm) && (
                <button type="button" onClick={clearFilters} title={t('common.clearFilters')} className="p-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"><XCircleIcon className="h-5 w-5" /></button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Institutions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('institutions.institution')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('institutions.code')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('institutions.district')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('institutions.contact')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('institutions.status')}
                </th>
                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('institutions.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <span className="ml-2 text-gray-600">{t('common.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : institutions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    {t('institutions.noInstitutions')}
                  </td>
                </tr>
              ) : (
                institutions.map((institution) => (
                  <tr key={institution.id} className="hover:bg-gray-50">
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <BuildingIcon className="h-4 w-4 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {translateInstitutionName(institution.name, currentLanguage || 'fr')}
                          </div>
                          <div className="text-sm text-gray-500">
                            {institution.address}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {institution.code}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-900">
                        {translateDistrictName(institution.district_name, currentLanguage || 'fr')}
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center gap-1">
                          <PhoneIcon className="h-3 w-3 text-gray-400" />
                          {institution.phone}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <MailIcon className="h-3 w-3 text-gray-400" />
                          {institution.email}
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : 'text-left'}`}>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        institution.is_active === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {institution.is_active === 1 ? t('institutions.active') : t('institutions.inactive')}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditInstitution(institution)}
                          className="text-primary-600 hover:text-primary-900"
                          title={t('institutions.edit')}
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteInstitution(institution.id, translateInstitutionName(institution.name, currentLanguage || 'fr'))}
                          className="text-red-600 hover:text-red-900"
                          title={t('institutions.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && institutions.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                  currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('institutions.previous')}
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                  currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {t('institutions.next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('institutions.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('institutions.to')}{' '}
                  <span className="font-medium">{Math.min(currentPage * limit, total)}</span> {t('institutions.of')}{' '}
                  <span className="font-medium">{total}</span> {t('institutions.results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                      currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span
                          key={page}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                      currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Institution Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('institutions.addInstitution')}</h3>
              <button onClick={closeAddModal} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.district')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={newInstitution.districtId}
                  onChange={(e) => setNewInstitution(prev => ({ ...prev, districtId: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.districtId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={districtsLoading}
                >
                  <option value="">{districtsLoading ? t('institutions.loadingDistricts') : t('institutions.selectDistrict')}</option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {translateDistrictName(district.name, currentLanguage || 'fr')}
                    </option>
                  ))}
                </select>
                {formErrors.districtId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.districtId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.institutionCode')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newInstitution.code}
                  onChange={(e) => setNewInstitution(prev => ({ ...prev, code: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="MAR001"
                />
                {formErrors.code && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.address')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newInstitution.address}
                  onChange={(e) => setNewInstitution(prev => ({ ...prev, address: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.address ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formErrors.address && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.address}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.phone')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newInstitution.phone}
                  onChange={(e) => setNewInstitution(prev => ({ ...prev, phone: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="+212..."
                />
                {formErrors.phone && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newInstitution.email}
                  onChange={(e) => setNewInstitution(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    formErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="info@example.com"
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">{t('institutions.translations')}</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('institutions.nameFr')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newInstitution.translations.fr}
                      onChange={(e) => setNewInstitution(prev => ({
                        ...prev,
                        translations: { ...prev.translations, fr: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_fr ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.translation_fr && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_fr}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('institutions.nameEn')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newInstitution.translations.en}
                      onChange={(e) => setNewInstitution(prev => ({
                        ...prev,
                        translations: { ...prev.translations, en: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_en ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formErrors.translation_en && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_en}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('institutions.nameAr')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newInstitution.translations.ar}
                      onChange={(e) => setNewInstitution(prev => ({
                        ...prev,
                        translations: { ...prev.translations, ar: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        formErrors.translation_ar ? 'border-red-500' : 'border-gray-300'
                      }`}
                      dir="rtl"
                    />
                    {formErrors.translation_ar && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.translation_ar}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeAddModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isAdding}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={addInstitution}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                disabled={isAdding}
              >
                {isAdding ? t('institutions.adding') : t('institutions.addInstitution')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Institution Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('institutions.editInstitution')}</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-500">
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.institutionName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingInstitution.name}
                  onChange={(e) => setEditingInstitution(prev => ({ ...prev, name: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.institutionCode')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingInstitution.code}
                  onChange={(e) => setEditingInstitution(prev => ({ ...prev, code: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.code && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.code}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.address')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingInstitution.address}
                  onChange={(e) => setEditingInstitution(prev => ({ ...prev, address: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.address ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.address && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.address}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.phone')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingInstitution.phone}
                  onChange={(e) => setEditingInstitution(prev => ({ ...prev, phone: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.phone && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('institutions.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={editingInstitution.email}
                  onChange={(e) => setEditingInstitution(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    editFormErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {editFormErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{editFormErrors.email}</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={isEditing}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={updateInstitution}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                disabled={isEditing}
              >
                {isEditing ? t('institutions.updating') : t('institutions.update')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Institutions;
